export const flattenColumns = (columns) => {
    if (!Array.isArray(columns))
        return [];
    const flat = [];
    columns.forEach((col) => {
        if (col?.children && Array.isArray(col.children) && col.children.length > 0) {
            flat.push(col); // Parent column
            col.children.forEach((child) => {
                if (child?.id) {
                    flat.push({ ...child, parentId: col.id, isChild: true });
                }
            });
        }
        else if (col?.id) {
            flat.push(col);
        }
    });
    return flat;
};
export const getAllColumnIds = (columns) => {
    if (!Array.isArray(columns))
        return [];
    const ids = [];
    columns.forEach((col) => {
        if (col?.id) {
            ids.push(col.id);
        }
        if (col?.children && Array.isArray(col.children)) {
            col.children.forEach((child) => {
                if (child?.id) {
                    ids.push(child.id);
                }
            });
        }
    });
    return ids;
};
export const normalizeColumns = (columns = [], hiddenColumns = new Set(), columnOrder = null, pinnedColumns = { left: [], right: [] }) => {
    if (!Array.isArray(columns)) {
        return { leftPinned: [], unpinned: [], rightPinned: [], all: [] };
    }
    let normalized = [...columns];
    // Apply column order if provided
    if (Array.isArray(columnOrder) && columnOrder.length > 0) {
        const orderMap = new Map();
        columnOrder.forEach((id, index) => {
            if (id) {
                orderMap.set(id, index);
            }
        });
        normalized.sort((a, b) => {
            if (!a?.id || !b?.id)
                return 0;
            const aIndex = orderMap.get(a.id) ?? Infinity;
            const bIndex = orderMap.get(b.id) ?? Infinity;
            return aIndex - bIndex;
        });
    }
    normalized = normalized.reduce((acc, col) => {
        if (!col?.id)
            return acc;
        if (hiddenColumns.has(col.id))
            return acc;
        if (Array.isArray(col.children) && col.children.length > 0) {
            const visibleChildren = col.children.filter((child) => child?.id && !hiddenColumns.has(child.id));
            if (visibleChildren.length === 0)
                return acc;
            // Only clone when something was actually removed (preserve identity otherwise).
            acc.push(visibleChildren.length === col.children.length ? col : { ...col, children: visibleChildren });
            return acc;
        }
        acc.push(col);
        return acc;
    }, []);
    // Separate pinned columns
    const leftPinned = [];
    const rightPinned = [];
    const unpinned = [];
    normalized.forEach((col) => {
        if (!col?.id)
            return;
        const leftPinnedArray = Array.isArray(pinnedColumns?.left) ? pinnedColumns.left : [];
        const rightPinnedArray = Array.isArray(pinnedColumns?.right) ? pinnedColumns.right : [];
        if (leftPinnedArray.includes(col.id)) {
            leftPinned.push(col);
        }
        else if (rightPinnedArray.includes(col.id)) {
            rightPinned.push(col);
        }
        else {
            unpinned.push(col);
        }
    });
    return { leftPinned, unpinned, rightPinned, all: normalized };
};
export const toSet = (value) => {
    if (value instanceof Set)
        return value;
    if (Array.isArray(value))
        return new Set(value);
    return new Set();
};
export const getColumnWidth = (column, defaultWidth = "auto") => {
    if (!column)
        return defaultWidth;
    return column.width || defaultWidth;
};
export const getColumnAlign = (column, defaultAlign = "left") => {
    if (!column)
        return defaultAlign;
    return column.align || defaultAlign;
};
export const hasGroupedColumns = (columns) => {
    if (!Array.isArray(columns))
        return false;
    return columns.some((col) => col?.children && Array.isArray(col.children) && col.children.length > 0);
};
export const getMaxColumnDepth = (columns) => {
    if (!Array.isArray(columns))
        return 1;
    return Math.max(...columns.map((col) => (col?.children && Array.isArray(col.children) && col.children.length > 0) ? 2 : 1), 1);
};
